import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Package, Inbox, Lock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
  const [activeTab, setActiveTab] = useState('categories');
  
  const [products, setProducts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [categories, setCategories] = useState([]);
  
  const [newProduct, setNewProduct] = useState({
    name: '',
    category_slug: '',
    description: '',
    image_url: '',
    featured: false,
  });

  const [newCategory, setNewCategory] = useState({
    name: '',
    slug: '',
    description: '',
    image_url: '',
  });

  useEffect(() => {
    if (token) {
      setIsAuthenticated(true);
      fetchData();
    }
  }, [token, activeTab]);

  const fetchData = async () => {
    try {
      if (activeTab === 'categories') {
        const res = await axios.get(`${API}/categories`);
        setCategories(res.data);
      } else if (activeTab === 'products') {
        const res = await axios.get(`${API}/products`);
        setProducts(res.data);
        const catRes = await axios.get(`${API}/categories`);
        setCategories(catRes.data);
      } else if (activeTab === 'inquiries') {
        const res = await axios.get(`${API}/inquiries`, {
          headers: { 'X-Admin-Token': token },
        });
        setInquiries(res.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/login`, credentials);
      setToken(res.data.token);
      localStorage.setItem('admin_token', res.data.token);
      setIsAuthenticated(true);
      toast.success('Login successful');
    } catch (error) {
      toast.error('Invalid credentials');
    }
  };

  const handleLogout = () => {
    setToken('');
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/categories`, newCategory, {
        headers: { 'X-Admin-Token': token },
      });
      toast.success('Category added successfully');
      setNewCategory({
        name: '',
        slug: '',
        description: '',
        image_url: '',
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to add category');
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/products`, newProduct, {
        headers: { 'X-Admin-Token': token },
      });
      toast.success('Product added successfully');
      setNewProduct({
        name: '',
        category_slug: '',
        description: '',
        image_url: '',
        featured: false,
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to add product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API}/products/${productId}`, {
        headers: { 'X-Admin-Token': token },
      });
      toast.success('Product deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface py-24">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md px-6"
        >
          <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-xl">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-accent/10 text-accent flex items-center justify-center">
                <Lock size={32} />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-center mb-6" data-testid="admin-login-title">Admin Login</h1>
            <form onSubmit={handleLogin} data-testid="admin-login-form">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Username</label>
                  <input
                    type="text"
                    value={credentials.username}
                    onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                    required
                    data-testid="admin-username-input"
                    className="w-full h-12 bg-slate-50 border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 rounded-lg px-4 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Password</label>
                  <input
                    type="password"
                    value={credentials.password}
                    onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                    required
                    data-testid="admin-password-input"
                    className="w-full h-12 bg-slate-50 border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 rounded-lg px-4 outline-none"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="admin-login-submit"
                  className="w-full h-12 rounded-full bg-accent text-white font-medium hover:scale-105 transition-transform duration-200 shadow-xl shadow-accent/20"
                >
                  Login
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="py-24 md:py-32 bg-surface min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold" data-testid="admin-dashboard-title">Admin Dashboard</h1>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            className="h-10 px-6 rounded-full bg-primary text-white font-medium hover:bg-primary/90"
          >
            Logout
          </button>
        </div>

        <div className="flex space-x-4 mb-8 border-b border-border">
          <button
            onClick={() => setActiveTab('categories')}
            data-testid="admin-tab-categories"
            className={`pb-3 px-4 font-medium transition-colors ${
              activeTab === 'categories'
                ? 'text-accent border-b-2 border-accent'
                : 'text-muted hover:text-primary'
            }`}
          >
            <Package className="inline mr-2" size={20} />
            Categories
          </button>
          <button
            onClick={() => setActiveTab('products')}
            data-testid="admin-tab-products"
            className={`pb-3 px-4 font-medium transition-colors ${
              activeTab === 'products'
                ? 'text-accent border-b-2 border-accent'
                : 'text-muted hover:text-primary'
            }`}
          >
            <Package className="inline mr-2" size={20} />
            Products
          </button>
          <button
            onClick={() => setActiveTab('inquiries')}
            data-testid="admin-tab-inquiries"
            className={`pb-3 px-4 font-medium transition-colors ${
              activeTab === 'inquiries'
                ? 'text-accent border-b-2 border-accent'
                : 'text-muted hover:text-primary'
            }`}
          >
            <Inbox className="inline mr-2" size={20} />
            Inquiries
          </button>
        </div>

        {activeTab === 'categories' && (
          <div>
            <div className="bg-white border border-slate-100 rounded-2xl p-8 mb-8">
              <h2 className="text-2xl font-semibold mb-6" data-testid="admin-add-category-title">Add New Category</h2>
              <form onSubmit={handleAddCategory} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Category Name (e.g., Tech Gadgets)"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  required
                  data-testid="admin-category-name-input"
                  className="h-12 bg-slate-50 border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 rounded-lg px-4 outline-none"
                />
                <input
                  type="text"
                  placeholder="Category Slug (e.g., tech-gadgets)"
                  value={newCategory.slug}
                  onChange={(e) => setNewCategory({ ...newCategory, slug: e.target.value })}
                  required
                  data-testid="admin-category-slug-input"
                  className="h-12 bg-slate-50 border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 rounded-lg px-4 outline-none"
                />
                <input
                  type="url"
                  placeholder="Category Image URL"
                  value={newCategory.image_url}
                  onChange={(e) => setNewCategory({ ...newCategory, image_url: e.target.value })}
                  required
                  data-testid="admin-category-image-input"
                  className="h-12 bg-slate-50 border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 rounded-lg px-4 outline-none md:col-span-2"
                />
                <textarea
                  placeholder="Category Description"
                  value={newCategory.description}
                  onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                  required
                  rows={3}
                  data-testid="admin-category-description-input"
                  className="md:col-span-2 bg-slate-50 border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 rounded-lg px-4 py-3 outline-none resize-none"
                ></textarea>
                <button
                  type="submit"
                  data-testid="admin-add-category-submit"
                  className="md:col-span-2 h-12 rounded-full bg-accent text-white font-medium hover:scale-105 transition-transform duration-200 shadow-xl shadow-accent/20 flex items-center justify-center"
                >
                  <Plus className="mr-2" size={20} />
                  Add Category
                </button>
              </form>
            </div>

            <div className="bg-white border border-slate-100 rounded-2xl p-8">
              <h2 className="text-2xl font-semibold mb-6" data-testid="admin-categories-list-title">Categories ({categories.length})</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((category, index) => (
                  <div
                    key={category.id || category.slug}
                    data-testid={`admin-category-item-${index}`}
                    className="p-4 bg-surface rounded-lg border border-slate-100"
                  >
                    <img
                      src={category.image_url}
                      alt={category.name}
                      className="w-full h-32 object-cover rounded-lg mb-3"
                    />
                    <h3 className="font-semibold mb-1">{category.name}</h3>
                    <p className="text-xs text-muted mb-1">Slug: {category.slug}</p>
                    <p className="text-sm text-muted line-clamp-2">{category.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'products' && (
          <div>
            <div className="bg-white border border-slate-100 rounded-2xl p-8 mb-8">
              <h2 className="text-2xl font-semibold mb-6" data-testid="admin-add-product-title">Add New Product</h2>
              <form onSubmit={handleAddProduct} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Product Name"
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                  required
                  data-testid="admin-product-name-input"
                  className="h-12 bg-slate-50 border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 rounded-lg px-4 outline-none"
                />
                <select
                  value={newProduct.category_slug}
                  onChange={(e) => setNewProduct({ ...newProduct, category_slug: e.target.value })}
                  required
                  data-testid="admin-product-category-select"
                  className="h-12 bg-slate-50 border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 rounded-lg px-4 outline-none"
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.slug} value={cat.slug}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                <input
                  type="url"
                  placeholder="Image URL"
                  value={newProduct.image_url}
                  onChange={(e) => setNewProduct({ ...newProduct, image_url: e.target.value })}
                  required
                  data-testid="admin-product-image-input"
                  className="h-12 bg-slate-50 border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 rounded-lg px-4 outline-none"
                />
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={newProduct.featured}
                    onChange={(e) => setNewProduct({ ...newProduct, featured: e.target.checked })}
                    data-testid="admin-product-featured-checkbox"
                    className="w-5 h-5 rounded"
                  />
                  <label className="text-sm font-medium">Featured Product</label>
                </div>
                <textarea
                  placeholder="Product Description"
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                  required
                  rows={3}
                  data-testid="admin-product-description-input"
                  className="md:col-span-2 bg-slate-50 border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 rounded-lg px-4 py-3 outline-none resize-none"
                ></textarea>
                <button
                  type="submit"
                  data-testid="admin-add-product-submit"
                  className="md:col-span-2 h-12 rounded-full bg-accent text-white font-medium hover:scale-105 transition-transform duration-200 shadow-xl shadow-accent/20 flex items-center justify-center"
                >
                  <Plus className="mr-2" size={20} />
                  Add Product
                </button>
              </form>
            </div>

            <div className="bg-white border border-slate-100 rounded-2xl p-8">
              <h2 className="text-2xl font-semibold mb-6" data-testid="admin-products-list-title">Products ({products.length})</h2>
              <div className="space-y-4">
                {products.map((product, index) => (
                  <div
                    key={product.id}
                    data-testid={`admin-product-item-${index}`}
                    className="flex items-center justify-between p-4 bg-surface rounded-lg border border-slate-100"
                  >
                    <div className="flex items-center space-x-4">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div>
                        <h3 className="font-semibold">{product.name}</h3>
                        <p className="text-sm text-muted">{product.category_slug}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteProduct(product.id)}
                      data-testid={`admin-delete-product-${index}`}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'inquiries' && (
          <div className="bg-white border border-slate-100 rounded-2xl p-8">
            <h2 className="text-2xl font-semibold mb-6" data-testid="admin-inquiries-title">Inquiries ({inquiries.length})</h2>
            <div className="space-y-4">
              {inquiries.map((inquiry, index) => (
                <div
                  key={inquiry.id}
                  data-testid={`admin-inquiry-item-${index}`}
                  className="p-6 bg-surface rounded-lg border border-slate-100"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-muted mb-1">Name</p>
                      <p className="font-semibold">{inquiry.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted mb-1">Company</p>
                      <p className="font-semibold">{inquiry.company}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted mb-1">Email</p>
                      <p className="font-semibold">{inquiry.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted mb-1">Phone</p>
                      <p className="font-semibold">{inquiry.phone}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-muted mb-1">Requirement</p>
                    <p className="text-sm">{inquiry.requirement}</p>
                  </div>
                  <p className="text-xs text-muted mt-4">
                    Received: {new Date(inquiry.created_at).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
